/**
 * Tiny wrapper around the Telegram WebApp SDK loaded from
 * https://telegram.org/js/telegram-web-app.js (linked in index.html).
 *
 * We deliberately don't depend on @twa-dev/sdk so this module is safe to
 * import in non-browser test environments.
 */
import { useEffect } from 'react'

export interface TelegramThemeParams {
  bg_color?: string
  text_color?: string
  hint_color?: string
  link_color?: string
  button_color?: string
  button_text_color?: string
  secondary_bg_color?: string
  [key: string]: string | undefined
}

export interface TelegramWebApp {
  initData: string
  /** Client platform ("ios", "android", "tdesktop", "weba", ...); used only for telemetry. */
  platform?: string
  /** Telegram client's WebApp API version (e.g. "7.0"); used only for telemetry. */
  version?: string
  initDataUnsafe: {
    user?: {
      id: number
      first_name: string
      username?: string
      language_code?: string
      is_premium?: boolean
    }
    auth_date?: number
    hash?: string
    start_param?: string
  }
  themeParams: TelegramThemeParams
  colorScheme: 'light' | 'dark'
  ready(): void
  expand(): void
  close(): void
  onEvent(event: string, cb: () => void): void
  /** True if the client's WebApp API version is >= the given version
   * (e.g. "8.0"). Use before calling any method newer than the baseline
   * this app otherwise assumes, since the SDK loads unversioned from a CDN
   * (see the file header) — the method exists in the JS object regardless
   * of the user's actual Telegram client version, and calling an
   * unsupported one throws WebAppMethodUnsupported instead of no-op'ing. */
  isVersionAtLeast(version: string): boolean
  /** Hands url to the native Telegram client to fetch and save as
   * file_name (Bot API 8.0+) — see downloadFile() below for why this is
   * used instead of a plain <a download> or a fetch()/blob approach. */
  downloadFile(params: { url: string; file_name: string }, callback?: (accepted: boolean) => void): void
  /** Opens url in the system browser (or an in-app browser tab, depending
   * on the client) without closing the Mini App. This is the officially
   * documented way to hand an external link out of a Mini App — a bare
   * `window.open()`/`<a target="_blank">` is not reliably honored by
   * Telegram's in-app WebView, see openExternalLink() below. */
  openLink(url: string, options?: { try_instant_view?: boolean }): void
  HapticFeedback: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void
    notificationOccurred(type: 'error' | 'success' | 'warning'): void
    selectionChanged(): void
  }
  /**
   * Telegram's native back button (shown in the client's own chrome). While
   * visible, the Android client also routes the hardware back button / back
   * gesture to `onClick`'s callback instead of closing the Mini App — see
   * useTelegramBackButton below, which is the whole point of this binding.
   */
  BackButton: {
    isVisible: boolean
    show(): void
    hide(): void
    onClick(cb: () => void): void
    offClick(cb: () => void): void
  }
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp }
  }
}

/** Returns the WebApp object if running inside Telegram, else undefined. */
export function tg(): TelegramWebApp | undefined {
  return typeof window === 'undefined' ? undefined : window.Telegram?.WebApp
}

/** Returns the raw initData query string, or "" outside Telegram. */
export function getInitData(): string {
  return tg()?.initData ?? ''
}

/** Returns the startapp parameter when the Mini App was opened via deeplink. */
export function getStartParam(): string | undefined {
  return tg()?.initDataUnsafe.start_param
}

/**
 * Hands url to the system browser via Telegram's native openLink. NOT a
 * bare `window.open()`/`<a target="_blank">` — Telegram's in-app WebView
 * does not reliably honor those for cross-origin resources (a known Mini
 * App gotcha): instead of failing cleanly, it can end up trying to
 * navigate a sandboxed popup to a bare resource with no page to render,
 * which just hangs (this is what "muter muter" — spinning with no result
 * — turned out to be, the first time this shipped without openLink).
 *
 * Falls back to window.open only when there's no Telegram context at all
 * (e.g. opening the Mini App URL directly in a plain desktop browser
 * during dev) — there's nothing else to hand the URL to in that case.
 */
export function openExternalLink(url: string): void {
  const app = tg()
  if (app) {
    app.openLink(url)
    return
  }
  window.open(url, '_blank', 'noopener,noreferrer')
}

/**
 * Saves url to the user's device as fileName. Uses Telegram's native
 * WebApp.downloadFile (Bot API 8.0+) so the request is made by the native
 * client, not this WebView — required because externally-hosted images we
 * link to (e.g. the Saku Rupiah QRIS image, see BuyVIPPage/BuyCrushPage)
 * have no CORS headers, so an in-app fetch()/blob approach isn't viable,
 * and a plain `<a download>` is unreliable for cross-origin resources
 * inside Telegram's in-app browser.
 *
 * Falls back to openExternalLink (manual save from the opened image) when
 * downloadFile is unsupported: either isVersionAtLeast('8.0') is false
 * (old client, or no Telegram context at all), or — the case the
 * try/catch below exists for — the client reports 8.0+ but doesn't
 * actually implement this specific method (a real parity gap between
 * Telegram clients), which throws WebAppMethodUnsupported synchronously.
 *
 * Deliberately does NOT treat the downloadFile callback's `accepted`
 * parameter as a failure signal — accepted:false means the user declined
 * the native save prompt, and popping an unwanted browser tab after
 * someone taps "No" would be worse than doing nothing.
 */
export function downloadFile(url: string, fileName: string): void {
  const app = tg()
  if (app && app.isVersionAtLeast('8.0')) {
    try {
      app.downloadFile({ url, file_name: fileName })
      return
    } catch {
      // Fall through to the link-opening fallback below.
    }
  }
  openExternalLink(url)
}

/** Boots the WebApp: marks ready, expands viewport, syncs theme variables. */
export function initTelegram(): void {
  const app = tg()
  if (!app) return
  app.ready()
  app.expand()
  applyThemeVars(app.themeParams)
  app.onEvent('themeChanged', () => applyThemeVars(app.themeParams))
}

const themeVarMap: Record<keyof TelegramThemeParams, string> = {
  bg_color: '--tg-bg-color',
  text_color: '--tg-text-color',
  hint_color: '--tg-hint-color',
  link_color: '--tg-link-color',
  button_color: '--tg-button-color',
  button_text_color: '--tg-button-text-color',
  secondary_bg_color: '--tg-secondary-bg-color',
}

export function applyThemeVars(params: TelegramThemeParams): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  for (const [k, css] of Object.entries(themeVarMap)) {
    const v = params[k]
    if (typeof v === 'string' && v) {
      root.style.setProperty(css, v)
    }
  }
}

/**
 * Shows Telegram's native BackButton for the lifetime of the calling
 * component and routes it (plus, critically, the Android hardware back
 * button / back gesture while the button is visible) to `onBack`. Pass
 * `null` to skip entirely — used by the root screen, which has no "back".
 *
 * Safe no-op outside Telegram (tg() returns undefined in tests/plain
 * browsers), so callers never need to guard this themselves.
 */
export function useTelegramBackButton(onBack: (() => void) | null): void {
  useEffect(() => {
    const app = tg()
    if (!app || !onBack) return
    app.BackButton.show()
    app.BackButton.onClick(onBack)
    return () => {
      app.BackButton.offClick(onBack)
      app.BackButton.hide()
    }
  }, [onBack])
}
